import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { apiService, ExecutionHistoryDetail } from '../services/apiService';
import { queryClient } from '../queryClient';
import LazyResponseBody from './LazyResponseBody';
import { formatAppTimestamp } from '../utils/dateTimeUtils';
import {
  historyDetailQueryKey,
  isLatestHistorySelection,
} from '../utils/historyExecutionIdentity';

interface HistoryExecutionDetailPanelProps {
  historyId: number;
}

const emptyHint = 'Not stored for this historical execution';

function displayOrHint(value?: string | null): string {
  if (value == null) return emptyHint;
  const trimmed = String(value).trim();
  return trimmed.length > 0 ? trimmed : emptyHint;
}

/**
 * Lightweight Global History detail: renders the SAVED execution snapshot from GET /history/{historyId}.
 * Does not mount Collections or mutate the current request.
 */
const HistoryExecutionDetailPanel: React.FC<HistoryExecutionDetailPanelProps> = ({ historyId }) => {
  const navigate = useNavigate();
  const [detail, setDetail] = useState<ExecutionHistoryDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const selectedHistoryIdRef = useRef(historyId);

  useEffect(() => {
    selectedHistoryIdRef.current = historyId;
  }, [historyId]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      // Keep prior detail only while loading the same id (cache hit feels instant).
      const cached = queryClient.getQueryData(historyDetailQueryKey(historyId)) as
        | ExecutionHistoryDetail
        | undefined;
      if (cached && cached.id === historyId) {
        setDetail(cached);
      } else {
        setDetail(null);
      }
      try {
        const data = (await queryClient.fetchQuery({
          queryKey: historyDetailQueryKey(historyId),
          queryFn: () => apiService.getHistoryDetail(historyId),
          staleTime: Infinity,
        })) as ExecutionHistoryDetail;
        if (cancelled) return;
        if (!isLatestHistorySelection(selectedHistoryIdRef.current, historyId)) return;
        if (!data || data.id !== historyId) {
          setDetail(null);
          setError('No history details found.');
          return;
        }
        setDetail(data);
      } catch {
        if (cancelled) return;
        if (!isLatestHistorySelection(selectedHistoryIdRef.current, historyId)) return;
        setDetail(null);
        setError('Failed to load history details.');
      } finally {
        if (!cancelled && isLatestHistorySelection(selectedHistoryIdRef.current, historyId)) {
          setLoading(false);
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [historyId]);

  const openCurrentRequest = () => {
    if (!detail?.requestId) return;
    const params = new URLSearchParams();
    if (detail.collectionId != null) {
      params.set('collectionId', String(detail.collectionId));
    }
    params.set('requestId', String(detail.requestId));
    navigate(`/collections?${params.toString()}`);
  };

  const pass =
    detail?.statusCode != null && detail.statusCode >= 200 && detail.statusCode < 300;

  return (
    <Paper
      elevation={0}
      sx={{
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          flexWrap: 'wrap',
          flexShrink: 0,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }} noWrap>
            {detail?.requestName || 'Execution details'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {detail?.collectionName || '—'}
            {detail?.createdAt ? ` • ${formatAppTimestamp(detail.createdAt, '—')}` : ''}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          onClick={openCurrentRequest}
          disabled={!detail?.requestId}
        >
          Open Current Request
        </Button>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 2 }}>
        {loading && !detail ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
            <CircularProgress />
          </Box>
        ) : error && !detail ? (
          <Typography color="error">{error}</Typography>
        ) : detail ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <CircularProgress size={18} />
              </Box>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="subtitle1" sx={{ wordBreak: 'break-all' }}>
                {detail.method} {detail.url}
              </Typography>
              <Chip
                label={pass ? 'PASS' : 'FAIL'}
                color={pass ? 'success' : 'error'}
                size="small"
              />
            </Box>
            <Typography variant="body2" color="text.secondary">
              Status: {detail.statusCode ?? '—'} {detail.statusText || ''}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Response Time: {detail.responseTime != null ? `${detail.responseTime} ms` : '—'}
            </Typography>
            {detail.responseSize != null && (
              <Typography variant="body2" color="text.secondary">
                Response Size: {detail.responseSize} bytes
              </Typography>
            )}
            {detail.dataSetId != null && (
              <Typography variant="body2" color="text.secondary">
                Dataset ID: {detail.dataSetId}
              </Typography>
            )}

            <Divider />

            <Typography variant="subtitle2">Request Headers (Executed)</Typography>
            <LazyResponseBody
              rawBody={displayOrHint(detail.executedHeaders || detail.requestHeadersJson)}
              height="120px"
              copyLabel="Copy request headers"
            />

            <Typography variant="subtitle2">Request Body (Executed)</Typography>
            <LazyResponseBody
              rawBody={displayOrHint(detail.executedRequestBody || detail.requestBodyJson)}
              height="120px"
              copyLabel="Copy request body"
            />

            {(detail.executedQueryParams || '').trim() ? (
              <>
                <Typography variant="subtitle2">Query Params (Executed)</Typography>
                <LazyResponseBody
                  rawBody={detail.executedQueryParams || ''}
                  height="80px"
                  copyLabel="Copy query params"
                />
              </>
            ) : null}

            {(detail.executedPathVariables || '').trim() ? (
              <>
                <Typography variant="subtitle2">Path Variables (Executed)</Typography>
                <LazyResponseBody
                  rawBody={detail.executedPathVariables || ''}
                  height="80px"
                  copyLabel="Copy path variables"
                />
              </>
            ) : null}

            <Typography variant="subtitle2">Response Headers</Typography>
            <LazyResponseBody
              rawBody={displayOrHint(detail.responseHeadersJson)}
              height="120px"
              copyLabel="Copy response headers"
            />

            <Typography variant="subtitle2">Response Body (Executed)</Typography>
            <LazyResponseBody
              rawBody={displayOrHint(detail.executedResponseBody || detail.responseBodyJson)}
              height="260px"
              copyLabel="Copy response body"
            />

            <Typography variant="subtitle2">Assertions Result</Typography>
            <LazyResponseBody
              rawBody={displayOrHint(detail.assertionsResultJson)}
              height="120px"
              copyLabel="Copy assertions result"
            />
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No history details found.
          </Typography>
        )}
      </Box>
    </Paper>
  );
};

export default HistoryExecutionDetailPanel;
